import React, { useEffect, useState } from "react";
import { colors, displayName } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, labelStyle, linkBtn } from "../clients/ui.js";
import { STATUS_ORDER, STATUS_META, PRIORITY_ORDER, PRIORITY_META, CLIENT_OWNER } from "./crmLogic.js";

const fmtDay = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

// A text field that saves when it loses focus (or on Enter), only if the value changed.
function BlurInput({ label, value, onCommit, placeholder }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const commit = () => { if (v.trim() !== (value || "")) onCommit(v.trim()); };
  return <input aria-label={label} style={inputStyle} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />;
}

// One client's track with one contact (spec 2026-11-03): status, priority, goal, owner, next
// step, and this client's activity log. Other clients are named only — never their notes.
export default function TrackPanel({ track, contact, otherClientNames = [], users = [], onSave, onAddActivity, onEditActivity, onRemoveActivity, onEditContact, onRemove, onClose }) {
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(null); // { id, text }
  const uName = (id) => { const u = users.find((x) => x.id === id || x.email === id); return u ? displayName(u) : "Someone"; };
  const activity = [...(track.activity || [])].sort((a, b) => (a.at < b.at ? 1 : -1));
  const row = { display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 };
  const add = async () => { if (!note.trim()) return; if ((await onAddActivity(note.trim())) !== false) setNote(""); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div data-testid="track-panel" onClick={(e) => e.stopPropagation()}
        style={{ width: 440, maxWidth: "92vw", height: "100%", background: colors.surface, borderLeft: `1px solid ${colors.border}`, padding: 22, overflow: "auto", boxShadow: "-16px 0 40px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>{contact?.name || "(deleted contact)"}</h3>
            {contact && <div style={{ fontSize: 12.5, color: colors.textDim, marginTop: 2 }}>{[contact.organization, contact.role, contact.base].filter(Boolean).join(" · ")}</div>}
            {contact && <div style={{ fontSize: 12.5, color: colors.textMuted, marginTop: 4 }}>{[contact.email, contact.phone].filter(Boolean).join(" · ")}</div>}
            {contact && <button style={{ ...linkBtn, marginTop: 6 }} onClick={onEditContact}>Edit contact details</button>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: colors.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 18 }}>
          <label style={row}><span style={labelStyle}>Status</span>
            <select aria-label="Status" style={inputStyle} value={track.status} onChange={(e) => onSave({ status: e.target.value })}>
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </label>
          <label style={row}><span style={labelStyle}>Priority</span>
            <select aria-label="Priority" style={inputStyle} value={track.priority || "med"} onChange={(e) => onSave({ priority: e.target.value })}>
              {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
          </label>
        </div>
        <label style={row}><span style={labelStyle}>Goal</span>
          <BlurInput label="Goal" value={track.goal} placeholder="e.g. AFRL lists a counter-UAS topic" onCommit={(goal) => onSave({ goal })} />
        </label>
        <label style={row}><span style={labelStyle}>Working it</span>
          <select aria-label="Working it" style={inputStyle} value={track.ownerUserId || ""} onChange={(e) => onSave({ ownerUserId: e.target.value || null })}>
            <option value="">Unassigned</option>
            <option value={CLIENT_OWNER}>Client</option>
            {users.map((u) => <option key={u.id} value={u.id}>{displayName(u)}</option>)}
          </select>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 10 }}>
          <label style={row}><span style={labelStyle}>Next step</span>
            <BlurInput label="Next step" value={track.nextStep} placeholder="e.g. Send capability brief" onCommit={(nextStep) => onSave({ nextStep })} />
          </label>
          <label style={row}><span style={labelStyle}>Date</span>
            <input type="date" aria-label="Next step date" style={inputStyle} value={track.nextStepDate || ""} onChange={(e) => onSave({ nextStepDate: e.target.value || null })} />
          </label>
        </div>

        <div style={{ ...labelStyle, marginTop: 10, marginBottom: 8 }}>Activity</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input style={inputStyle} placeholder="Log a call, email, meeting…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <button style={primaryBtn} onClick={add} disabled={!note.trim()}>Add</button>
        </div>
        <div style={{ fontSize: 11, color: colors.textDim, margin: "4px 0 10px" }}>Activity can appear in this client’s update meetings.</div>
        {activity.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>Nothing logged yet.</div>}
        {activity.map((a) => (
          <div key={a.id} style={{ borderLeft: `2px solid ${colors.borderLight}`, padding: "2px 0 10px 10px", marginLeft: 3 }}>
            <div style={{ fontSize: 11.5, color: colors.textDim }}>
              {fmtDay(a.at)} · {uName(a.by)}
              {editing?.id !== a.id && <>
                {" · "}<button style={{ ...linkBtn, fontSize: 11 }} onClick={() => setEditing({ id: a.id, text: a.text })}>Edit</button>
                {" · "}<button style={{ ...linkBtn, fontSize: 11, color: colors.danger }} onClick={() => onRemoveActivity(a.id)}>Delete</button>
              </>}
            </div>
            {editing?.id === a.id ? (
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <input aria-label="Edit activity" style={inputStyle} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
                <button style={primaryBtn} onClick={async () => { if ((await onEditActivity(a.id, editing.text)) !== false) setEditing(null); }}>Save</button>
                <button style={ghostBtn} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            ) : <div style={{ fontSize: 13, color: colors.text, whiteSpace: "pre-wrap", marginTop: 2 }}>{a.text}</div>}
          </div>
        ))}

        {otherClientNames.length > 0 && (
          <div style={{ fontSize: 12, color: colors.textDim, marginTop: 16 }}>
            Also working this contact: {otherClientNames.map((n) => <span key={n} style={{ display: "inline-block", fontSize: 11.5, padding: "2px 8px", borderRadius: 10, background: colors.accentDim, color: "#93c5fd", margin: "0 4px 4px 0" }}>{n}</span>)}
            <div style={{ fontSize: 11 }}>Their notes stay on their own CRM tab.</div>
          </div>
        )}
        <button onClick={onRemove} style={{ ...ghostBtn, color: colors.danger, borderColor: "rgba(239,68,68,0.4)", marginTop: 20 }}>Remove from this client</button>
      </div>
    </div>
  );
}
